import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/catalog", label: "Products" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-lg" : "bg-white/95 backdrop-blur-xl border-b border-gray-100"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center gap-4" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Imperial Exim Trading LLC" className="h-16 md:h-20 w-auto" />
          <div className="leading-tight">
            <span className="block text-[#000050] font-black text-sm md:text-base tracking-wide" style={{ fontFamily: 'Chivo, sans-serif' }}>IMPERIAL EXIM</span>
            <span className="block text-[#DC143C] font-bold text-xs md:text-sm tracking-[0.15em]" style={{ fontFamily: 'Chivo, sans-serif' }}>TRADING LLC</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                location.pathname === link.href
                  ? "text-[#DC143C]"
                  : "text-[#000050] hover:text-[#DC143C]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/catalog"
            data-testid="nav-cta-explore"
            className="bg-[#DC143C] text-white text-sm font-semibold tracking-wide px-5 py-2.5 rounded-md hover:bg-[#B01030] transition-colors"
          >
            Explore Products
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-gray-700 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav
          data-testid="mobile-nav"
          className="md:hidden bg-white border-t border-gray-200 px-6 py-4 space-y-3 animate-fade-in"
        >
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              className={`block text-sm font-medium py-2 ${
                location.pathname === link.href ? "text-[#DC143C]" : "text-gray-600"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/catalog"
            data-testid="mobile-nav-cta"
            className="block bg-[#DC143C] text-white text-sm font-semibold tracking-wide px-5 py-2.5 rounded-md text-center mt-2"
          >
            Explore Products
          </Link>
        </nav>
      )}
    </header>
  );
}
